import { useMemo } from 'react';
import { useHorizontalScrollAffordance } from '../../hooks/useHorizontalScrollAffordance';
import type { Province, ScienceInvestment } from '../../types/province';
import { StatCard } from '../ui/StatCard';
import { GlanceCard } from '../shell/GlanceCard';
import { MetricRow } from '../shell/MetricRow';
import { COLORS } from '../../constants/theme';
import { formatEnumLabel, formatNumber } from '../../utils/format';
import { buildInvestmentPlannerData } from './investmentPlanner';
import { stripLeadingPercentage } from './scienceEffectFormatting';

interface SciencesOverviewTabProps {
  province: Province;
  investments: ScienceInvestment[];
  investmentProgress: Record<string, number>;
  compact?: boolean;
}

/**
 * SF-161: ported/adapted council/tabs/ScienceTab.tsx content (stat cards + Technology table +
 * 24-day "Learning the Arts" planner), shared by DesktopSciencesShell (compact=false) and
 * MobileSciencesShell (compact=true) via the ReleaseTable compact-prop precedent. Not a literal
 * port — see spec Component 1 / Key Implementation Note #1.
 */
export function SciencesOverviewTab({ province, investments, investmentProgress, compact = false }: SciencesOverviewTabProps) {
  const sci = province.sciences;
  const scienceTypes = sci?.scienceTypes ?? [];
  const books = sci?.books ?? {};
  const progress = sci?.progress ?? {};
  const costPreview = sci?.costPreview ?? null;
  const researchRate = sci?.researchRate ?? 'NO_RESEARCH';

  const totalBooks = Object.values(books).reduce((s, b) => s + b, 0);
  const totalInProgress = Object.values(investmentProgress).reduce((s, b) => s + b, 0);

  // `|| formatEnumLabel(...)` (not `??`) so a metadata entry with an empty/falsy `name` also
  // falls back, not just a genuinely-missing entry — a defensive data-quality guard beyond
  // MobileSciencesShell.tsx's original `?? ` precedent (find-returns-undefined case).
  const resolveLabel = (scienceType: string) =>
    scienceTypes.find((st) => st.type === scienceType)?.name || formatEnumLabel(scienceType);

  if (compact) {
    return (
      <div className="flex flex-col gap-3">
        <GlanceCard title="Research">
          <MetricRow
            label="Research Rate"
            value={formatEnumLabel(researchRate)}
            unit=""
            weight="primary"
          />
          <div className="text-[11px] -mt-1.5" style={{ color: COLORS.text.tertiary }}>
            {costPreview ? `${formatNumber(Math.round(costPreview.booksPerTick))} books/day` : 'no research'}
          </div>
          <MetricRow label="Total Books" value={formatNumber(Math.round(totalBooks))} unit="" weight="secondary" />
          <MetricRow label="In Progress" value={formatNumber(Math.round(totalInProgress))} unit="" weight="tertiary" />
        </GlanceCard>

        <TechnologyTable
          scienceTypes={scienceTypes}
          books={books}
          progress={progress}
          investmentProgress={investmentProgress}
          resolveLabel={resolveLabel}
          compact
        />

        <InvestmentPlannerCard investments={investments} resolveLabel={resolveLabel} compact />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <div className="w-36">
          <StatCard
            label="Research Rate"
            value={formatEnumLabel(researchRate)}
            sub={costPreview ? `${formatNumber(Math.round(costPreview.booksPerTick))} books/day` : 'no research'}
            borderless
          />
        </div>
        <div className="w-36">
          <StatCard
            label="Total Books"
            value={formatNumber(Math.round(totalBooks))}
            sub="completed"
            borderless
          />
        </div>
        <div className="w-36">
          <StatCard
            label="In Progress"
            value={formatNumber(Math.round(totalInProgress))}
            sub="books investing"
            borderless
          />
        </div>
      </div>

      <TechnologyTable
        scienceTypes={scienceTypes}
        books={books}
        progress={progress}
        investmentProgress={investmentProgress}
        resolveLabel={resolveLabel}
        compact={false}
      />

      <InvestmentPlannerCard investments={investments} resolveLabel={resolveLabel} compact={false} />
    </div>
  );
}

// ─── Technology table ────────────────────────────────────────────────────

interface TechnologyTableProps {
  scienceTypes: Province['sciences']['scienceTypes'];
  books: Record<string, number>;
  progress: Record<string, { effectPercentage: number; currentBooks: number }>;
  investmentProgress: Record<string, number>;
  resolveLabel: (scienceType: string) => string;
  compact: boolean;
}

function TechnologyTable({ scienceTypes, books, progress, investmentProgress, resolveLabel, compact }: TechnologyTableProps) {
  if (compact) {
    return (
      <div className="rounded-lg overflow-hidden" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
        {scienceTypes.map((st, idx) => {
          const known = books[st.type] ?? 0;
          const inProgress = investmentProgress[st.type] ?? 0;
          const rowProgress = progress[st.type];
          const isEmpty = known === 0 && inProgress === 0;
          const label = resolveLabel(st.type);

          return (
            <div
              key={st.type}
              className="flex flex-col gap-1 px-3 py-2 hover:bg-[rgba(255,255,255,0.06)]"
              style={{
                borderBottom: idx < scienceTypes.length - 1 ? `1px solid ${COLORS.border}` : undefined,
                opacity: isEmpty ? 0.4 : 1,
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm" style={{ color: COLORS.text.primary }}>{label}</span>
                <span className="text-sm font-mono text-right" style={{ color: isEmpty ? COLORS.text.tertiary : COLORS.positive }}>
                  {rowProgress ? `+${rowProgress.effectPercentage.toFixed(1)}%` : '—'}
                  {rowProgress && st.effectDescription ? (
                    <span style={{ color: COLORS.text.tertiary }}> {stripLeadingPercentage(st.effectDescription)}</span>
                  ) : null}
                </span>
              </div>
              <div className="text-[11px]" style={{ color: COLORS.text.tertiary }}>
                Known {formatNumber(Math.round(known))} · In Progress {inProgress > 0 ? formatNumber(Math.round(inProgress)) : '—'}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  const totalBooks = scienceTypes.reduce((s, st) => s + (books[st.type] ?? 0), 0);
  const totalInProgress = scienceTypes.reduce((s, st) => s + (investmentProgress[st.type] ?? 0), 0);

  return (
    <div
      className="rounded overflow-hidden"
      style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
    >
      <div
        className="grid items-center px-3 py-1.5"
        style={{
          gridTemplateColumns: '180px 80px 80px 16px 1fr',
          backgroundColor: COLORS.sectionHeader,
        }}
      >
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.text.primary }}>
          Technology
        </span>
        <span className="text-right text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>Known</span>
        <span className="text-right text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>In Prog</span>
        <span />
        <span className="text-left text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>Effect</span>
      </div>

      {scienceTypes.map((st) => {
        const known = books[st.type] ?? 0;
        const inProgress = investmentProgress[st.type] ?? 0;
        const rowProgress = progress[st.type];
        const isEmpty = known === 0 && inProgress === 0;
        const label = resolveLabel(st.type);

        return (
          <div
            key={st.type}
            className="grid items-center px-3 py-1.5 hover:bg-[rgba(255,255,255,0.06)]"
            style={{
              gridTemplateColumns: '180px 80px 80px 16px 1fr',
              borderBottom: `1px solid ${COLORS.border}`,
              opacity: isEmpty ? 0.4 : 1,
            }}
          >
            <span className="text-sm" style={{ color: COLORS.text.primary }}>{label}</span>
            <span className="text-right font-mono text-sm" style={{ color: COLORS.text.secondary }}>
              {formatNumber(Math.round(known))}
            </span>
            <span className="text-right font-mono text-sm" style={{ color: COLORS.text.secondary }}>
              {inProgress > 0 ? formatNumber(Math.round(inProgress)) : '—'}
            </span>
            <span />
            <span className="text-left text-sm font-mono" style={{ color: isEmpty ? COLORS.text.tertiary : COLORS.positive }}>
              {rowProgress ? `+${rowProgress.effectPercentage.toFixed(1)}%` : '—'}
              {rowProgress && st.effectDescription ? (
                <span style={{ color: COLORS.text.tertiary }}> {stripLeadingPercentage(st.effectDescription)}</span>
              ) : null}
            </span>
          </div>
        );
      })}

      {/* Total row */}
      <div
        className="grid items-center px-3 py-1.5"
        style={{
          gridTemplateColumns: '180px 80px 80px 16px 1fr',
          backgroundColor: COLORS.sectionHeader,
        }}
      >
        <span className="text-sm font-semibold" style={{ color: COLORS.text.primary }}>Total</span>
        <span className="text-right font-mono text-sm font-semibold" style={{ color: COLORS.text.primary }}>
          {formatNumber(Math.round(totalBooks))}
        </span>
        <span className="text-right font-mono text-sm font-semibold" style={{ color: COLORS.text.primary }}>
          {totalInProgress > 0 ? formatNumber(Math.round(totalInProgress)) : '—'}
        </span>
        <span />
        <span />
      </div>
    </div>
  );
}

// ─── "Learning the Arts" investment planner ─────────────────────────────

interface InvestmentPlannerCardProps {
  investments: ScienceInvestment[];
  resolveLabel: (scienceType: string) => string;
  compact: boolean;
}

function InvestmentPlannerCard({ investments, resolveLabel, compact }: InvestmentPlannerCardProps) {
  const planner = useMemo(() => buildInvestmentPlannerData(investments), [investments]);
  const labelMinWidth = compact ? 90 : 120;
  const { scrollRef, canScrollLeft, canScrollRight } = useHorizontalScrollAffordance<HTMLDivElement>();

  return (
    <div
      className="rounded overflow-hidden"
      style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
    >
      <div className="px-3 py-1.5" style={{ backgroundColor: COLORS.sectionHeader }}>
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.text.primary }}>
          Learning the Arts
        </span>
      </div>

      {planner ? (
        <div className="relative">
          {canScrollLeft && (
            <div
              aria-hidden="true"
              className="absolute left-0 top-0 bottom-0 z-10 pointer-events-none"
              style={{ width: 24, background: `linear-gradient(to right, ${COLORS.card}, transparent)` }}
            />
          )}
          {canScrollRight && (
            <div
              aria-hidden="true"
              className="absolute right-0 top-0 bottom-0 z-10 pointer-events-none"
              style={{ width: 24, background: `linear-gradient(to left, ${COLORS.card}, transparent)` }}
            />
          )}
          <div ref={scrollRef} style={{ overflowX: 'auto' }} tabIndex={0} role="region" aria-label="Investment schedule by day">
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <colgroup>
              <col style={{ minWidth: labelMinWidth }} />
              {(compact ? planner.days.filter((d) => planner.totals[d] > 0) : planner.days).map((day) => (
                <col key={day} style={{ minWidth: '28px' }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th
                  className="text-left px-2 py-1 text-[10px] uppercase tracking-wide"
                  style={{
                    color: COLORS.text.tertiary,
                    borderBottom: `1px solid ${COLORS.border}`,
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    backgroundColor: COLORS.sectionHeader,
                  }}
                >
                  Days until completed
                </th>
                {(compact ? planner.days.filter((d) => planner.totals[d] > 0) : planner.days).map((day) => (
                  <th
                    key={day}
                    className="text-center py-1 text-[10px] font-mono"
                    style={{ color: COLORS.text.tertiary, borderBottom: `1px solid ${COLORS.border}` }}
                  >
                    {day}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {planner.sciences.map((s) => (
                <tr key={s.scienceType} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <td
                    className="px-2 py-0.5 text-sm"
                    style={{
                      color: COLORS.text.primary,
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      backgroundColor: COLORS.card,
                    }}
                  >
                    {resolveLabel(s.scienceType)}
                  </td>
                  {(compact ? planner.days.filter((d) => planner.totals[d] > 0) : planner.days).map((day) => {
                    const incoming = s.byDay[day];
                    return (
                      <td
                        key={day}
                        className="text-center py-0.5 font-mono text-xs"
                        style={{ color: incoming ? COLORS.text.secondary : COLORS.text.tertiary }}
                      >
                        {incoming ? Math.round(incoming) : ''}
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr>
                <td
                  className="px-2 py-0.5 text-xs font-semibold"
                  style={{
                    color: COLORS.text.primary,
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    backgroundColor: COLORS.sectionHeader,
                  }}
                >
                  Total
                </td>
                {(compact ? planner.days.filter((d) => planner.totals[d] > 0) : planner.days).map((day) => (
                  <td
                    key={day}
                    className="text-center py-0.5 font-mono text-xs font-semibold"
                    style={{ color: COLORS.text.primary, backgroundColor: COLORS.sectionHeader }}
                  >
                    {planner.totals[day] ? Math.round(planner.totals[day]) : ''}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          </div>
        </div>
      ) : (
        <div className="px-3 py-3 text-sm" style={{ color: COLORS.text.tertiary }}>
          No research currently in progress.
        </div>
      )}
    </div>
  );
}
